// ============================================================================
// WORKFLOW CONFIG ADMIN PAGE
// System admin UI for managing n8n workflow URLs, enabled state, and triggers.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  workflowConfigService,
  ResolvedWorkflow,
  WorkflowTrigger,
} from '../services/workflowConfigService';
import {
  ToggleLeft,
  ToggleRight,
  RotateCcw,
  Pencil,
  Check,
  X,
  Timer,
  Zap,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Tv2,
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================================================
// URL Source Badge
// ============================================================================

const SourceBadge: React.FC<{ source: ResolvedWorkflow['webhookUrlSource'] }> = ({ source }) => {
  const styles: Record<string, string> = {
    db: 'bg-primary/10 text-primary border-primary/20',
    env: 'bg-slate-100 text-slate-500 border-slate-200',
    unset: 'bg-red-50 text-red-500 border-red-200',
  };
  const labels: Record<string, string> = { db: 'DB', env: 'ENV', unset: 'UNSET' };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${styles[source]}`}>
      {labels[source]}
    </span>
  );
};

// ============================================================================
// Trigger Config Modal
// ============================================================================

const TRIGGER_LABELS: Record<string, string> = {
  phase_transition: 'Phase Transition',
  session_start: 'Session Start',
  session_end: 'Session End',
  timer: 'Timer',
};

interface TriggerModalProps {
  workflow: ResolvedWorkflow;
  onSave: (triggers: WorkflowTrigger[]) => Promise<void>;
  onClose: () => void;
}

const TriggerModal: React.FC<TriggerModalProps> = ({ workflow, onSave, onClose }) => {
  const [triggers, setTriggers] = useState<WorkflowTrigger[]>(workflow.triggers);
  const [saving, setSaving] = useState(false);

  const getTrigger = (type: WorkflowTrigger['type']) => triggers.find(t => t.type === type);

  const upsertTrigger = (type: WorkflowTrigger['type'], patch: Partial<WorkflowTrigger>) => {
    setTriggers(prev => {
      const existing = prev.find(t => t.type === type);
      if (existing) return prev.map(t => t.type === type ? { ...t, ...patch } : t);
      return [...prev, { type, enabled: false, ...patch }];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(triggers); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-[#052831]">Trigger Config</h3>
            <p className="text-xs text-slate-500 mt-0.5">{workflow.name}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          {workflow.availableTriggers.map(triggerType => {
            const t = getTrigger(triggerType) ?? { type: triggerType, enabled: false };
            return (
              <div key={triggerType} className="border border-slate-200 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">{TRIGGER_LABELS[triggerType]}</span>
                  <button
                    onClick={() => upsertTrigger(triggerType, { enabled: !t.enabled })}
                    className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${t.enabled ? 'bg-primary' : 'bg-slate-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${t.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                {triggerType === 'timer' && t.enabled && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs text-slate-500 whitespace-nowrap">Interval (min)</label>
                    <input
                      type="number" min={1} max={60}
                      value={t.config?.intervalMinutes ?? 4}
                      onChange={e => upsertTrigger('timer', { config: { intervalMinutes: parseInt(e.target.value) || 4 } })}
                      className="w-20 text-sm border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 mt-5">
          <button
            onClick={handleSave} disabled={saving}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />} Save
          </button>
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Table Row
// ============================================================================

interface WorkflowRowProps {
  workflow: ResolvedWorkflow;
  onUpdate: (id: string, updated: ResolvedWorkflow) => void;
}

const WorkflowRow: React.FC<WorkflowRowProps> = ({ workflow, onUpdate }) => {
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState(workflow.webhookUrl ?? '');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showTriggers, setShowTriggers] = useState(false);

  const handleToggleEnabled = async () => {
    try {
      const updated = await workflowConfigService.update(workflow.id, { enabled: !workflow.enabled });
      onUpdate(workflow.id, updated);
      toast.success(`${workflow.name} ${updated.enabled ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to update'); }
  };

  const handleToggleCinematic = async () => {
    try {
      const updated = await workflowConfigService.update(workflow.id, { cinematicView: !workflow.cinematicView });
      onUpdate(workflow.id, updated);
      toast.success(`${workflow.name} cinematic view ${updated.cinematicView ? 'on' : 'off'}`);
    } catch { toast.error('Failed to update'); }
  };

  const handleSaveUrl = async () => {
    if (!urlDraft.trim()) { toast.error('URL cannot be empty — use Reset to revert to env var'); return; }
    setSaving(true);
    try {
      const updated = await workflowConfigService.update(workflow.id, { webhookUrl: urlDraft.trim() });
      onUpdate(workflow.id, updated);
      setEditingUrl(false);
      toast.success('URL updated');
    } catch { toast.error('Failed to update URL'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const updated = await workflowConfigService.reset(workflow.id);
      onUpdate(workflow.id, updated);
      setUrlDraft(updated.webhookUrl ?? '');
      setEditingUrl(false);
      toast.success(`${workflow.name} reset to defaults`);
    } catch { toast.error('Reset failed'); }
    finally { setResetting(false); }
  };

  const handleSaveTriggers = async (triggers: WorkflowTrigger[]) => {
    const updated = await workflowConfigService.update(workflow.id, { triggers });
    onUpdate(workflow.id, updated);
    toast.success('Triggers updated');
  };

  const activeTriggers = workflow.triggers.filter(t => t.enabled);

  return (
    <>
      <tr className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${!workflow.enabled ? 'opacity-50' : ''}`}>
        {/* Enable toggle */}
        <td className="py-3 pl-4 pr-2 w-8">
          <button onClick={handleToggleEnabled} className={`transition-colors ${workflow.enabled ? 'text-primary' : 'text-slate-300'}`} title={workflow.enabled ? 'Disable' : 'Enable'}>
            {workflow.enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
          </button>
        </td>

        {/* Name + description */}
        <td className="py-3 px-3 min-w-[160px]">
          <div className="font-medium text-sm text-[#052831]">{workflow.name}</div>
          <div className="text-[11px] text-slate-400 mt-0.5 leading-tight">{workflow.description}</div>
        </td>

        {/* URL */}
        <td className="py-3 px-3">
          {editingUrl ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={urlDraft}
                onChange={e => setUrlDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveUrl(); else if (e.key === 'Escape') { setEditingUrl(false); setUrlDraft(workflow.webhookUrl ?? ''); }}}
                className="flex-1 text-xs border border-primary/40 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono min-w-0"
              />
              <button onClick={handleSaveUrl} disabled={saving} className="shrink-0 flex items-center gap-0.5 px-2 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary-dark disabled:opacity-50">
                {saving ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} />}
              </button>
              <button onClick={() => { setEditingUrl(false); setUrlDraft(workflow.webhookUrl ?? ''); }} className="shrink-0 p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-50">
                <X size={11} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 group">
              <span className={`text-xs font-mono truncate max-w-xs ${workflow.webhookUrl ? 'text-slate-500' : 'text-slate-300 italic'}`} title={workflow.webhookUrl ?? undefined}>
                {workflow.webhookUrl ?? 'Not configured'}
              </span>
              <button onClick={() => { setUrlDraft(workflow.webhookUrl ?? ''); setEditingUrl(true); }} className="shrink-0 p-0.5 rounded hover:bg-slate-200 text-slate-400">
                <Pencil size={11} />
              </button>
            </div>
          )}
        </td>

        {/* Source badge */}
        <td className="py-3 px-3 w-14">
          <SourceBadge source={workflow.webhookUrlSource} />
        </td>

        {/* Active triggers */}
        <td className="py-3 px-3 w-40">
          <div className="flex flex-wrap gap-1">
            {activeTriggers.map(t => (
              <span key={t.type} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/5 text-primary text-[10px] font-medium border border-primary/10">
                {t.type === 'timer' ? <Timer size={9} /> : <Zap size={9} />}
                {t.type === 'timer' && t.config?.intervalMinutes ? `${t.config.intervalMinutes}m` : t.type.replace('_', ' ')}
              </span>
            ))}
          </div>
        </td>

        {/* Cinematic toggle */}
        <td className="py-3 px-3 w-12">
          <button
            onClick={handleToggleCinematic}
            className={`transition-colors ${workflow.cinematicView ? 'text-primary' : 'text-slate-200'}`}
            title={workflow.cinematicView ? 'Remove from cinematic view' : 'Show in cinematic view'}
          >
            <Tv2 size={16} />
          </button>
        </td>

        {/* Actions */}
        <td className="py-3 pl-3 pr-4 w-28">
          <div className="flex items-center gap-1 justify-end">
            {workflow.availableTriggers.length > 0 && (
              <button onClick={() => setShowTriggers(true)} className="flex items-center gap-0.5 px-2 py-1 border border-slate-200 rounded-lg text-[11px] text-slate-500 hover:bg-slate-50 transition-colors">
                <Zap size={11} /> Triggers
              </button>
            )}
            {workflow.hasDbOverride && (
              <button onClick={handleReset} disabled={resetting} className="p-1 rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-50 disabled:opacity-50" title="Reset to defaults">
                {resetting ? <RefreshCw size={13} className="animate-spin" /> : <RotateCcw size={13} />}
              </button>
            )}
          </div>
        </td>
      </tr>

      {showTriggers && (
        <TriggerModal workflow={workflow} onSave={handleSaveTriggers} onClose={() => setShowTriggers(false)} />
      )}
    </>
  );
};

// ============================================================================
// Workflow groups
// ============================================================================

const GROUP_LABELS: Record<string, string> = {
  ambient: 'Ambient / Auto-triggered',
  session: 'Core Session Workflows',
  avatar: 'Avatar',
  device: 'Devices',
  report: 'Reports',
  kb: 'Knowledge Base',
  graph: 'Graph & Sync',
  governance: 'Governance',
  miro: 'Miro',
  sandbox: 'Sandbox',
  worker: 'Workers',
  task: 'Session Tasks',
};

const WORKFLOW_GROUPS: Record<string, string[]> = {
  ambient: ['mind-bubble'],
  session: ['choose-avatars', 'tts', 'requirements-extraction', 'requirements-consolidation', 'facilitator-chat', 'facilitator-guidance', 'contribution-feedback'],
  avatar: ['avatar-performance-query', 'avatar-performance-sync'],
  device: ['device-api', 'device-image-vision'],
  report: ['report-assess', 'report-generate', 'report-export'],
  kb: ['kb-ingest', 'kb-search', 'kb-delete'],
  graph: ['graph-query', 'participant-graph-sync'],
  governance: ['credo-sync'],
  miro: ['miro-graph-sync', 'miro-frame-ingest', 'miro-cross-frame-link'],
  sandbox: ['sandbox-provision', 'sandbox-decommission', 'sandbox-deploy'],
  worker: ['claude-code-worker', 'worker-approval-decision'],
  task: [
    'task-graph-sync', 'task-session-graph-sync', 'task-project-graph-sync', 'task-contribution-graph-sync',
    'task-phase-summary-sync', 'task-ideation-graph-sync', 'task-governance-graph-sync', 'task-analytics-graph-sync',
    'task-decision-graph-sync', 'task-device-image-graph-sync', 'task-kb-document-graph-sync',
    'task-build-artifact-graph-sync', 'task-task-output-graph-sync', 'task-credo-governance-sync',
    'task-requirements-extraction', 'task-decision-detection', 'task-customer-research',
    'task-competitive-analysis', 'task-social-pulse', 'task-architecture-diagram',
    'task-bcaf-assessment', 'task-build-with-ai',
  ],
};

// ============================================================================
// Main Page
// ============================================================================

const WorkflowConfigAdmin: React.FC = () => {
  const [workflows, setWorkflows] = useState<ResolvedWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set(['task']));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setWorkflows(await workflowConfigService.listAll());
    } catch {
      setError('Failed to load workflow config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = (id: string, updated: ResolvedWorkflow) => {
    setWorkflows(prev => prev.map(w => w.id === id ? updated : w));
  };

  const toggleGroup = (group: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group); else next.add(group);
      return next;
    });
  };

  const getWorkflow = (id: string) => workflows.find(w => w.id === id);
  const unsetCount = workflows.filter(w => w.webhookUrlSource === 'unset').length;
  const disabledCount = workflows.filter(w => !w.enabled).length;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#052831]">Workflow Config</h1>
          <p className="text-sm text-slate-500 mt-1">Manage n8n webhook URLs and session trigger configuration</p>
        </div>
        <button onClick={load} disabled={loading} className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Stats */}
      {!loading && workflows.length > 0 && (
        <div className="flex gap-3 mb-6">
          <div className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm">
            <span className="font-bold text-[#052831]">{workflows.length}</span>
            <span className="text-slate-500">workflows</span>
          </div>
          {unsetCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-sm">
              <span className="font-bold text-red-600">{unsetCount}</span>
              <span className="text-red-500">not configured</span>
            </div>
          )}
          {disabledCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm">
              <span className="font-bold text-slate-500">{disabledCount}</span>
              <span className="text-slate-400">disabled</span>
            </div>
          )}
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-sm text-red-600">{error}</div>}

      {loading && (
        <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
          <RefreshCw size={18} className="animate-spin mr-2" /> Loading workflows...
        </div>
      )}

      {/* Table per group */}
      {!loading && Object.entries(WORKFLOW_GROUPS).map(([groupKey, ids]) => {
        const groupWorkflows = ids.map(getWorkflow).filter((w): w is ResolvedWorkflow => w !== undefined);
        if (groupWorkflows.length === 0) return null;
        const isCollapsed = collapsedGroups.has(groupKey);

        return (
          <div key={groupKey} className="mb-6">
            <button onClick={() => toggleGroup(groupKey)} className="flex items-center gap-2 w-full text-left mb-2 group">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 group-hover:text-slate-600 transition-colors">
                {GROUP_LABELS[groupKey]}
              </span>
              <span className="text-[10px] text-slate-300 bg-slate-100 px-1.5 py-0.5 rounded-full">{groupWorkflows.length}</span>
              <div className="flex-1 h-px bg-slate-100" />
              {isCollapsed ? <ChevronDown size={14} className="text-slate-300" /> : <ChevronUp size={14} className="text-slate-300" />}
            </button>

            {!isCollapsed && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full">
                  <colgroup>
                    <col style={{ width: '36px' }} />
                    <col style={{ width: '260px' }} />
                    <col />
                    <col style={{ width: '56px' }} />
                    <col style={{ width: '160px' }} />
                    <col style={{ width: '44px' }} />
                    <col style={{ width: '120px' }} />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                      <th className="py-2 pl-4 pr-2" />
                      <th className="py-2 px-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Workflow</th>
                      <th className="py-2 px-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Webhook URL</th>
                      <th className="py-2 px-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Source</th>
                      <th className="py-2 px-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Triggers</th>
                      <th className="py-2 px-3 text-center text-[11px] font-semibold text-slate-400 uppercase tracking-wide" title="Show in cinematic view"><Tv2 size={11} className="inline" /></th>
                      <th className="py-2 pl-3 pr-4 text-right text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupWorkflows.map(w => (
                      <WorkflowRow key={w.id} workflow={w} onUpdate={handleUpdate} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WorkflowConfigAdmin;
